import { useCallback, useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { addBreadcrumb, captureException, captureMessage } from '@sentry/react';
import { QUERY_CARRIER_ASSIGNMENT_BY_ID, QUERY_ORDER_SAVE_GUARD } from '@/graphql/queries/orders';
import { ORDER_BY_ID } from '@/components/OrderDetailsPage/queries';
import { isStale, liveConflict, shouldGuardSave } from '../utils/staleSave';
import { useOrderChangeFeed } from './useOrderChangeFeed';
import { assignmentPatch } from '../utils/carrierAssignment';

/** How long the open-time version read may take before it is abandoned and its result ignored. */
const VERSION_CHECK_TIMEOUT_MS = 5000;

/** How long the open-time load of the latest order may take before it is abandoned and its result ignored. */
const LOAD_TIMEOUT_MS = 10000;

/**
 * Which version of the order the Edit Order modal is editing, and whether it is still current.
 *
 * Owns the base version, the conflict that blocked the last Done, the reload state, the live
 * change feed, and the user's own in-flight writes. Pure helpers stay in `../utils/staleSave.js`.
 * The feed subscription is `useOrderChangeFeed.js`.
 *
 * On open it reads the stored version (`resync: 'checking'`). If the stored version differs from the
 * prop's, the cached order is stale. The guard then loads the latest order into the draft with no
 * prompt (`resync: 'loading'`), but only if the user hasn't pressed or typed yet (`userStartedRef`);
 * otherwise nothing loads and the live prompt or Done guard behaves as usual. Adopting only the
 * version would let Done write the stale cached fields back, so the version and data come from one
 * read. The load also refreshes the cache behind the caller, whose `order` prop then catches up to
 * the loaded version; that update keeps the notice and does not re-run the check. The check is
 * abandoned after 5 s and the load after 10 s; failures leave the cached form in
 * place, each open where the version moved or the check didn't finish logs `edit_order_open_resync`
 * with its outcome, with the live feed and the Done guard as the backstop.
 *
 * Known limitation: `markSaved` runs once `onSave` resolves, and some callers resolve even when
 * their mutation failed and only show a notification (OrderDetailsPage). The live prompt then stays
 * silent until the modal closes. The Done check still blocks a stale save, so nothing is lost.
 *
 * @param {object} args
 * @param {object} args.order The order prop.
 * @param {boolean} args.open Whether the modal is open.
 * @param {boolean} args.isNewOrder True for an order that is not saved yet.
 * @param {boolean} [args.readOnly] True in read-only mode, where the feed is not subscribed.
 * @param {object} args.draft Return value of `useOrderDraft`.
 * @param {{ current: boolean }} [args.userStartedRef] `useUserStarted`'s flag: true once the user has pressed or typed in the modal.
 * @param {boolean} args.saveInflight True while Done's save runs, so the prompt stays hidden.
 * @param {{ refetchOrder?: Function, onError?: Function }} args.callbacks The caller's callbacks.
 * @returns {{
 *   resync: 'checking'|'loading'|null,
 *   resyncLoaded: boolean,
 *   dismissResyncNotice: () => void,
 *   liveChange: object|null,
 *   versionConflict: { variant: 'live'|'stale', row: object }|null,
 *   reloading: boolean,
 *   reloadError: string|null,
 *   checkBeforeSave: () => Promise<{ ok: boolean, stored?: object|null }>,
 *   markSaved: () => void,
 *   reload: () => Promise<void>,
 *   keepEditing: () => void,
 *   runOwnWrite: <T>(write: () => Promise<T>) => Promise<T>,
 *   refetchOrder: (leg?: string) => Promise<void>,
 * }}
 */
export const useOrderVersionGuard = ({
    order,
    open,
    isNewOrder,
    readOnly,
    draft,
    userStartedRef,
    saveInflight,
    callbacks,
}) => {
    // Execute resolves with `{ data, error }` rather than rejecting, so callers check `error`. The
    // guard read is not cached: see QUERY_ORDER_SAVE_GUARD. The reload is cached on purpose, so the
    // page behind the modal refreshes too.
    const [fetchSaveGuard] = useLazyQuery(QUERY_ORDER_SAVE_GUARD, { fetchPolicy: 'no-cache' });
    const [fetchLatestOrder, { loading: reloading }] = useLazyQuery(ORDER_BY_ID, { fetchPolicy: 'network-only' });
    // Re-reads the columns `carrier/unassign` clears. `network-only` so it reaches Hasura rather
    // than answering from the cache it is there to correct.
    const [fetchCarrierAssignment] = useLazyQuery(QUERY_CARRIER_ASSIGNMENT_BY_ID, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err);
            captureException(err);
        },
    });

    const { clone, patch: patchDraft, replaceWith } = draft;

    // The version the modal is editing, and the conflict that blocked the last Done.
    const [baseVersion, setBaseVersion] = useState(order?.last_modified_at || null);
    const [conflict, setConflict] = useState(null);
    const [reloadError, setReloadError] = useState(null);
    // Count of the user's own server writes in flight (AdminTab's unassign), from the request
    // through the read-back that adopts the new version, so the feed's echo of that write doesn't
    // raise the prompt. A count because the read-back nests inside the write. State, not a ref:
    // clearing it must re-render the derived prompt.
    const [ownWrites, setOwnWrites] = useState(0);
    // Set once Done's save has been handed to the caller, until the modal closes or gets a new
    // order. Some callers close only when their mutation completes (DispatchPlan), so the feed's
    // echo of this save can land while the modal is still open. The Done guard still catches a
    // real change if the save fails and the modal stays open.
    const [savedAwaitingClose, setSavedAwaitingClose] = useState(false);
    // The open-time re-sync step. State, not a ref: clearing it must re-render the derived prompt.
    const [resync, setResync] = useState(null);
    // True once the open-time re-sync loaded a newer version, until dismissed, closed, or a new order.
    const [resyncLoaded, setResyncLoaded] = useState(false);
    // The open-time re-sync's load, from the start of the load until the modal closes:
    // `{ order_id, from, to }`, where `from` is the prop version that was checked and `to` the
    // version loaded. The load writes `orders` to the normalized cache, so callers reading the
    // cache (OrderDetailsPage) pass a new `order` prop at `to` moments later. That prop only catches
    // up to what the modal already shows, so it must not hide the notice or re-run the check.
    const [resynced, setResynced] = useState(null);
    const propVersion = order?.last_modified_at || null;
    const caughtUp = !!resynced && resynced.order_id === order?.order_id && resynced.to === propVersion;
    // The version the open-time check runs for: a caught-up prop keeps the version that was checked.
    const checkedVersion = caughtUp ? resynced.from : propVersion;
    // The open-time effect's current key, for a load that finishes after the prop's version changed.
    const checkedVersionRef = useRef(checkedVersion);
    checkedVersionRef.current = checkedVersion;
    const liveRow = useOrderChangeFeed({ open, isNewOrder, readOnly, order });
    const liveChange =
        ownWrites > 0 || saveInflight || savedAwaitingClose || resync
            ? null
            : liveConflict({ baseVersion, live: liveRow });
    const lastLoggedVersion = useRef(null);

    useEffect(() => {
        setBaseVersion(order?.last_modified_at || null);
        setConflict(null);
        setReloadError(null);
        setSavedAwaitingClose(false);
        if (!caughtUp) setResyncLoaded(false);
    }, [order]);

    /**
     * Reads the latest stored order and applies it to the draft and base version. Resolves the
     * applied order, or false without applying when `isCancelled` says the result is no longer
     * wanted. Throws on failure.
     */
    const loadFresh = useCallback(
        async (isCancelled) => {
            const { data, error } = await fetchLatestOrder({ variables: { order_id: order.order_id } });
            if (error) throw error;
            const fresh = data?.orders?.[0];
            if (!fresh) throw new Error('Order not found');
            if (isCancelled?.()) return false;
            replaceWith(fresh);
            setBaseVersion(fresh.last_modified_at || null);
            setConflict(null);
            // Every load (open-time or Reload order) refreshes the caller's cache, so its prop
            // catches up to `to`. `from` is the open-time effect's current key, so catching up
            // doesn't change it, whether the prop got there before or after this point.
            setResynced({
                order_id: order.order_id,
                from: checkedVersionRef.current,
                to: fresh.last_modified_at || null,
            });
            return fresh;
        },
        [fetchLatestOrder, order, replaceWith]
    );
    // The open-time effect below is keyed on the order's identity, so it reads the latest closure here.
    const loadFreshRef = useRef(loadFresh);
    loadFreshRef.current = loadFresh;

    /**
     * Loads the latest stored order into the open modal. Unsaved field edits are discarded; the
     * typed note is separate state and survives. The read also refreshes the page behind the modal
     * through the normalized `orders` cache.
     */
    const reload = useCallback(async () => {
        setReloadError(null);
        addBreadcrumb({ category: 'edit_order_stale_save', message: 'reload_order' });
        try {
            await loadFresh();
        } catch (err) {
            console.error(err);
            captureException(err);
            setReloadError("Couldn't reload this order. Try again.");
        }
    }, [loadFresh]);

    // Read the stored version once per open. The cached version can be stale (writes that don't
    // select `last_modified_at`), and older fragments carry none. A matching version needs nothing;
    // a missing one is adopted. A moved version is not adopted on its own: the draft was built from
    // the same stale cache, so adopting it would let Done write old values back. Instead the latest
    // order is loaded, but only if the user hasn't started, so nothing they typed is replaced. Its
    // own effect, keyed on `open`, because callers set the order and `open` separately
    // (DispatchPlan), so the order can arrive while the modal is still closed. Keyed on
    // `checkedVersion`, not the prop's version, so the prop catching up to the loaded version
    // neither re-runs the check nor cancels a load still in flight. A later version still does.
    useEffect(() => {
        if (!open || !shouldGuardSave({ isNewOrder, order })) return;
        let cancelled = false;
        let timer;
        let phase = 'check';
        let storedVersion = null;
        const startedAt = Date.now();
        let loadStartedAt = null;
        // One Sentry message per open where the version moved or the check didn't complete.
        const logOutcome = (outcome) => {
            const now = Date.now();
            captureMessage('edit_order_open_resync', {
                level: 'info',
                extra: {
                    outcome,
                    order_id: order.order_id,
                    propVersion: order.last_modified_at || null,
                    storedVersion,
                    checkMs: (loadStartedAt || now) - startedAt,
                    loadMs: loadStartedAt ? now - loadStartedAt : null,
                    path: window.location.pathname,
                },
            });
        };
        // Abandons the current step: a late result is ignored through `cancelled`.
        const armTimeout = (ms) => {
            clearTimeout(timer);
            timer = setTimeout(() => {
                cancelled = true;
                logOutcome(phase === 'check' ? 'read_timeout' : 'load_timeout');
                setResync(null);
            }, ms);
        };
        const run = async () => {
            setResync('checking');
            armTimeout(VERSION_CHECK_TIMEOUT_MS);
            const { data, error } = await fetchSaveGuard({ variables: { order_id: order.order_id } });
            if (error) {
                clearTimeout(timer);
                console.error(error);
                captureException(error);
                if (!cancelled) {
                    logOutcome('read_failed');
                    setResync(null);
                }
                return;
            }
            if (cancelled) return;
            clearTimeout(timer);
            const stored = data?.order?.last_modified_at;
            storedVersion = stored || null;
            if (!stored || stored === order.last_modified_at) {
                setResync(null);
                return;
            }
            if (!order.last_modified_at) {
                setBaseVersion(stored);
                setResync(null);
                return;
            }
            if (userStartedRef?.current) {
                logOutcome('skipped_user_started');
                setResync(null);
                return;
            }
            phase = 'load';
            loadStartedAt = Date.now();
            // Set before the load, since the cache write can re-render the caller before it resolves.
            const from = order.last_modified_at;
            setResynced({ order_id: order.order_id, from, to: stored });
            setResync('loading');
            armTimeout(LOAD_TIMEOUT_MS);
            try {
                const fresh = await loadFreshRef.current(() => cancelled);
                if (fresh) {
                    setResyncLoaded(true);
                    logOutcome('loaded');
                }
            } catch (err) {
                console.error(err);
                captureException(err);
                if (!cancelled) logOutcome('load_failed');
            }
            clearTimeout(timer);
            if (!cancelled) setResync(null);
        };
        run();
        return () => {
            cancelled = true;
            clearTimeout(timer);
            setResync(null);
        };
    }, [open, order?.order_id, checkedVersion]);

    useEffect(() => {
        if (!open) {
            setSavedAwaitingClose(false);
            setResyncLoaded(false);
            setResynced(null);
        }
    }, [open]);

    /** Hides the "order changed after the page loaded" notice. */
    const dismissResyncNotice = useCallback(() => setResyncLoaded(false), []);

    // One Sentry message per base version, so feed re-deliveries don't repeat it.
    useEffect(() => {
        if (!liveChange || lastLoggedVersion.current === baseVersion) return;
        lastLoggedVersion.current = baseVersion;
        captureMessage('edit_order_live_change_detected', {
            level: 'info',
            extra: {
                order_id: order?.order_id,
                baseVersion,
                liveVersion: liveChange.last_modified_at,
                liveStatus: liveChange.order_status,
                yourStatus: clone?.order_status,
                path: window.location.pathname,
            },
        });
    }, [liveChange, baseVersion, order?.order_id, clone?.order_status]);

    /**
     * Runs one of the user's own server writes from inside the modal (AdminTab's unassign)
     * with the live prompt suppressed. Wrap the request and its read-back
     * together: the server commits before the request returns, so the feed can deliver the new
     * version before the read-back starts.
     */
    const runOwnWrite = useCallback(async (write) => {
        setOwnWrites((n) => n + 1);
        try {
            return await write();
        } finally {
            setOwnWrites((n) => n - 1);
        }
    }, []);

    /**
     * Exposed on the context as `refetchOrder`, which is what AdminTab calls after an unassign.
     *
     * It has to resync the clone itself, not just delegate to the caller: `clone` is only rebuilt
     * when the `order` prop's identity changes, and every caller but OrderDetailsPage holds that
     * order in its own useState, so refetching a page's list query leaves the modal on the old
     * snapshot. Writing the read into the normalized cache is what refreshes the page behind the
     * modal -- `orders` is keyed on order_id -- so callers need not pass anything.
     *
     * The caller's own refetch still runs first when there is one, for the queries a narrow read
     * cannot touch.
     *
     * `leg` scopes the patch to the leg that was actually torn down -- see `assignmentPatch`. It
     * deliberately stops here and is not forwarded: the caller's `refetchOrder` is an Apollo
     * `refetch`, whose first argument is a variables object it merges over the query's own, so a
     * bare string would spread to `{ 0: 'D', 1: 'I', ... }` and corrupt the read.
     */
    const refetchOrder = useCallback(
        (leg) =>
            runOwnWrite(async () => {
                await callbacks.refetchOrder?.();

                if (!order?.order_id) return;

                const { data, error } = await fetchCarrierAssignment({ variables: { order_id: order.order_id } });
                // Thrown so the caller can tell the user to reload: a clone still holding the cleared
                // carrier is what Done would write back.
                if (error) throw error;
                const patch = assignmentPatch(data?.order, leg);
                if (!patch) return;

                // Merged, not replaced: the operator may have edits open in other tabs, and the unassign
                // only owns these columns.
                patchDraft(patch);
                // The unassign is the user's own server write; it must not read as someone else's change.
                if (data?.order?.last_modified_at) setBaseVersion(data.order.last_modified_at);
            }),
        [runOwnWrite, callbacks.refetchOrder, order?.order_id, fetchCarrierAssignment, patchDraft]
    );

    /** Dismisses the stale-save dialog and keeps the user's edits. */
    const keepEditing = useCallback(() => {
        addBreadcrumb({ category: 'edit_order_stale_save', message: 'keep_editing' });
        setConflict(null);
        setReloadError(null);
    }, []);

    /**
     * The Done-time check: reads the stored order and compares its version to the base version.
     * Resolves `{ ok: true, stored }` to go ahead (`stored` is null on an unguarded order), or
     * `{ ok: false }` after a failed read (reported through `callbacks.onError`) or a stale
     * version (which sets `conflict`). Run it before any side effect so a blocked save leaves the
     * modal untouched.
     */
    const checkBeforeSave = useCallback(async () => {
        if (!shouldGuardSave({ isNewOrder, order })) return { ok: true, stored: null };

        let stored;
        try {
            const { data, error } = await fetchSaveGuard({ variables: { order_id: order.order_id } });
            if (error) throw error;
            stored = data?.order || null;
            if (!stored) throw new Error('Order not found');
        } catch (err) {
            console.error(err);
            captureMessage('edit_order_stale_save_guard_read_failed', {
                level: 'warning',
                extra: { order_id: order.order_id, path: window.location.pathname },
            });
            callbacks.onError?.("Couldn't confirm this order is up to date. Try again.");
            return { ok: false };
        }

        if (!baseVersion) {
            captureMessage('edit_order_stale_save_check_skipped', {
                level: 'warning',
                extra: { order_id: order.order_id, path: window.location.pathname },
            });
        } else if (isStale(baseVersion, stored)) {
            captureMessage('edit_order_stale_save_blocked', {
                level: 'info',
                extra: {
                    order_id: order.order_id,
                    baseVersion,
                    storedVersion: stored.last_modified_at,
                    storedStatus: stored.order_status,
                    modalStatus: clone.order_status,
                    path: window.location.pathname,
                },
            });
            setConflict(stored);
            return { ok: false };
        }
        return { ok: true, stored };
    }, [isNewOrder, order, fetchSaveGuard, baseVersion, callbacks.onError, clone.order_status]);

    /** Call once the save has been handed to the caller: silences the feed's echo of it. */
    const markSaved = useCallback(() => setSavedAwaitingClose(true), []);

    // What the dialog shows: the live change wins over the Done conflict, whose reload covers it.
    const versionConflict = liveChange
        ? { variant: 'live', row: liveChange }
        : conflict
        ? { variant: 'stale', row: conflict }
        : null;

    return {
        resync,
        resyncLoaded,
        dismissResyncNotice,
        liveChange,
        versionConflict,
        reloading,
        reloadError,
        checkBeforeSave,
        markSaved,
        reload,
        keepEditing,
        runOwnWrite,
        refetchOrder,
    };
};
